/**
 * Muzzle flash particle effects — shared across all tanks.
 */

import { MUZZLE } from '../../../constants';
import { configParticle, type ParticleCore } from '../types';
import { darken, lighten, warm } from '../color-utils';

/**
 * Heavy muzzle flash — grenade launcher thump. Two-phase: suck-in → spit-out.
 * Phase 0 (t=0): Particles rush INWARD toward barrel tip (vacuum compression).
 * Phase 1 (t=60ms): Enhanced outward blast (the thump).
 */
export function spawnHeavyMuzzleFlash(core: ParticleCore, x: number, y: number, angle: number, color: number): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const backCos = Math.cos(angle + Math.PI);
  const backSin = Math.sin(angle + Math.PI);

  // === PHASE 0: SUCK-IN (t=0ms) — vacuum compression at barrel tip ===

  // Inward-rushing particles from a ring around the tip
  // Sakurai contrast: mix bright energy + dark vacuum particles
  const suckCount = 14;
  for (let i = 0; i < suckCount; i++) {
    const ringAngle = (i / suckCount) * Math.PI * 2;
    const dist = 35 + Math.random() * 25; // spawn 35-60px away
    const spawnX = x + Math.cos(ringAngle) * dist;
    const spawnY = y + Math.sin(ringAngle) * dist;
    // Velocity aimed at the barrel tip
    const toTipAngle = Math.atan2(y - spawnY, x - spawnX);
    const speed = 12 + Math.random() * 8;
    const r = Math.random();
    const suckColor = r < 0.3 ? darken(color, 0.9)  // dark vacuum (Sakurai: mix dark)
      : r < 0.6 ? color                            // weapon identity
        : r < 0.8 ? 0xffffff                         // white energy
          : darken(color, 0.75);                        // deep shade
    const isDark = r < 0.3;
    core.spawnParticleAtAngle(
      spawnX, spawnY,
      toTipAngle, speed,
      suckColor,
      0.04 + Math.random() * 0.03,  // very brief — 40-70ms to reach center
      isDark ? 0.4 + Math.random() * 0.3 : 0.25 + Math.random() * 0.2,
      0, 0.92,
      !isDark, true,  // bright = ADD, dark = NORMAL
      isDark ? 0.6 : 0.85,
      2.5 + Math.random() * 1.5,  // stretch — velocity-aligned streaks
    );
  }

  // Compression point — bright gathering dot at barrel tip
  core.spawnParticle(x, y, 0xffffff, 0, 0, 0.05, 0.08, 0.5, 1.0, 0, 0.3, true, true, 0.9);
  core.spawnParticle(x, y, color, 0, 0, 0.06, 0.1, 1.0, 1.8, 0, 0.3, true, false, 0.7);

  // Dark inward shockwave — contracting ring (negative expansion = visual trick via short life + small radius)
  core.spawnShockwave(x, y, darken(color, 0.75), 20, 80, 2, 0.08);

  // === PHASE 1: SPIT-OUT (t=60ms) — enhanced muzzle blast (the thump) ===

  core.pendingBlasts.push({
    timestamp: Date.now() + 60,
    callback: () => {
      const M = MUZZLE;

      // Hot core flash — bigger than standard, concentrated power release
      for (let i = 0; i < M.FLASH_COUNT + 4; i++) {
        const r = Math.random();
        const flashColor = r < 0.12 ? darken(color, 0.9)  // dark contrast
          : r < 0.45 ? color
            : r < 0.65 ? warm(color, 0.5)
              : r < 0.8 ? lighten(warm(color, 0.4), 0.3)
                : 0xffffff;                          // white hot
        const isDark = r < 0.12;
        const off = Math.random() * 10;
        core.spawnParticle(
          x + cos * off, y + sin * off, flashColor,
          0, 1.5,
          0.06, 0.14,
          2.5, 5.0,         // larger flash — power release
          0, 0.35,
          !isDark, false,
          isDark ? 0.6 : 0.8,
        );
      }

      // Directional cone burst — more particles, more energy than standard
      const coneCount = M.CONE_COUNT + 8;
      for (let i = 0; i < coneCount; i++) {
        const spread = (Math.random() - 0.5) * 2 * M.CONE_SPREAD;
        const particleAngle = angle + spread;
        const speed = 14 + Math.random() * 16;  // faster than standard
        const r = Math.random();
        const coneColor = r < 0.4 ? color
          : r < 0.6 ? warm(color, 0.5)
            : r < 0.75 ? lighten(warm(color, 0.4), 0.3)
              : r < 0.9 ? 0xffffff
                : darken(color, 0.75);    // dark accent
        const off = 5 + Math.random() * 10;
        core.spawnParticleAtAngle(
          x + Math.cos(particleAngle) * off,
          y + Math.sin(particleAngle) * off,
          particleAngle, speed,
          coneColor,
          0.1 + Math.random() * 0.12,
          0.25 + Math.random() * 0.35,
          0, 0.93,
          true, true,
          0.9,
          3.5 + Math.random() * 3,   // longer streaks — more aggressive
        );
      }

      // Heavy sparks — gravity-arcing, wider spread
      for (let i = 0; i < M.SPARK_COUNT + 6; i++) {
        const spread = (Math.random() - 0.5) * 2 * M.SPARK_SPREAD;
        const sparkAngle = angle + spread;
        const speed = 6 + Math.random() * 14;
        const sparkColor = Math.random() < 0.35 ? color
          : Math.random() < 0.5 ? warm(color, 0.5)
            : Math.random() < 0.7 ? lighten(warm(color, 0.4), 0.3)
              : 0xffffff;
        const off = 3 + Math.random() * 6;
        core.spawnParticleAtAngle(
          x + Math.cos(sparkAngle) * off,
          y + Math.sin(sparkAngle) * off,
          sparkAngle, speed,
          sparkColor,
          0.2 + Math.random() * 0.25,
          0.3 + Math.random() * 0.5,
          0.25 + Math.random() * 0.15,
          0.95,
          true, true,
          0.85,
        );
      }

      // Micro-particles for density
      for (let i = 0; i < 12; i++) {
        const microAngle = angle + (Math.random() - 0.5) * 2 * M.SPARK_SPREAD;
        const microSpeed = 5 + Math.random() * 12;
        const off = 2 + Math.random() * 8;
        core.spawnParticleAtAngle(
          x + Math.cos(microAngle) * off,
          y + Math.sin(microAngle) * off,
          microAngle, microSpeed,
          Math.random() < 0.5 ? color : 0xffffff,
          0.06 + Math.random() * 0.08,
          0.08 + Math.random() * 0.12,
          0.1 + Math.random() * 0.1,
          0.92,
          true, true,
          0.7,
        );
      }

      // Bigger shockwaves — the thump
      core.spawnShockwave(x, y, color, M.SHOCKWAVE_RADIUS * 1.3, 350, 4, 0.22);
      core.spawnShockwave(x, y, warm(color, 0.3), M.SHOCKWAVE_RADIUS * 0.8, 250, 3, 0.15);
      // Dark outline ring for contrast
      core.spawnShockwave(x, y, darken(color, 0.8), M.SHOCKWAVE_RADIUS * 1.1, 300, 2, 0.18);

      // Impact lines — thicker, more aggressive
      const halfCount = Math.floor((M.IMPACT_LINE_COUNT + 2) / 2);
      for (let i = 0; i < M.IMPACT_LINE_COUNT + 2; i++) {
        const lineSpread = (i - halfCount) * M.IMPACT_LINE_SPREAD;
        const lineAngle = angle + lineSpread;
        const life = 0.14 + Math.random() * 0.06;
        const dark = Math.random() < 0.2;
        core.impactLines.push({
          x, y, angle: lineAngle,
          innerRadius: 5,
          outerRadius: 32 + Math.random() * 22,
          expandSpeed: 250 + Math.random() * 100,
          color: dark ? darken(color, 0.8) : (Math.random() < 0.4 ? 0xffffff : color),
          lineWidth: dark ? 4 : 3 + Math.random(),
          life, maxLife: life,
          freezeImmune: false,
        });
      }

      // Delayed smoke — heavier, more lingering (baton pass)
      core.pendingBlasts.push({
        timestamp: Date.now() + 40,
        callback: () => {
          const backAngle = angle + Math.PI;
          for (let i = 0; i < M.SMOKE_COUNT + 4; i++) {
            const smokeSpread = (Math.random() - 0.5) * Math.PI * 0.6;
            const smokeAngle = backAngle + smokeSpread;
            const r = Math.random();
            const smokeColor = r < 0.3 ? 0x112222
              : r < 0.5 ? 0x223333
                : r < 0.7 ? 0x333344
                  : 0x222233;
            const off = 3 + Math.random() * 6;
            core.spawnParticleAtAngle(
              x + Math.cos(smokeAngle) * off,
              y + Math.sin(smokeAngle) * off,
              smokeAngle,
              1.0 + Math.random() * 3,
              smokeColor,
              0.35 + Math.random() * 0.35,
              1.8 + Math.random() * 1.8,
              -0.015,
              0.982,
              false, false,
              0.55,
            );
          }
        },
      });
    },
  });
}

/** Directional muzzle flash — Sakurai-style launch effect at turret tip */
export function spawnMuzzleFlash(core: ParticleCore, x: number, y: number, angle: number, color: number): void {
  const M = MUZZLE;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // === Phase 1: Hot core flash — fiery, instant ===
  // NORMAL blend keeps fire colors true (ADD washes overlapping brights to white)
  // Sakurai: offset spawns along fire direction to reduce center pile-up
  for (let i = 0; i < M.FLASH_COUNT; i++) {
    const r = Math.random();
    const flashColor = r < 0.15 ? darken(color, 0.85)   // dark (Sakurai contrast)
      : r < 0.45 ? color                               // player identity — dominant
        : r < 0.65 ? warm(color, 0.5)                    // warm combustion
          : r < 0.8 ? lighten(warm(color, 0.4), 0.3)       // bright warm
            : darken(warm(color, 0.5), 0.3);                  // deep warm
    const isDark = r < 0.15;
    // Offset 0-8px along fire direction — spreads the flash forward
    const off = Math.random() * 8;
    core.spawnParticle(
      x + cos * off, y + sin * off, flashColor,
      0, 1.0,           // slight drift
      0.06, 0.12,       // brief
      2.0, 4.0,         // large but not overwhelming
      0, 0.35,          // no gravity, heavy friction
      false,            // NORMAL blend — prevents white washout
      false,            // soft glow texture
      isDark ? 0.65 : 0.75,
    );
  }

  // === Phase 2: Directional cone burst — forward fire energy ===
  // Sakurai: offset spawns along travel direction so they're already spread on birth
  // Micro-stagger: split into 2 sub-waves ~8ms apart (perceived density, less simultaneous load)
  const conePerWave = Math.ceil(M.CONE_COUNT / 2);
  for (let w = 0; w < 2; w++) {
    const waveSpawn = () => {
      for (let i = 0; i < conePerWave; i++) {
        const spread = (Math.random() - 0.5) * 2 * M.CONE_SPREAD;
        const particleAngle = angle + spread;
        const speed = 12 + Math.random() * 14;
        const r = Math.random();
        const coneColor = r < 0.4 ? color        // player identity
          : r < 0.6 ? warm(color, 0.5)            // warm fire
            : r < 0.75 ? lighten(warm(color, 0.4), 0.3)  // bright warm
              : r < 0.9 ? lighten(color, 0.5)       // hot bright
                : darken(warm(color, 0.5), 0.3);       // deep warm
        // Offset 4-12px along particle direction — avoids center waste
        const off = 4 + Math.random() * 8;
        core.spawnParticleAtAngle(
          x + Math.cos(particleAngle) * off,
          y + Math.sin(particleAngle) * off,
          particleAngle, speed,
          coneColor,
          0.1 + Math.random() * 0.12,  // 100-220ms life
          0.2 + Math.random() * 0.3,   // medium scale
          0, 0.93,                       // no gravity, some friction
          true, true,                    // ADD, hard glow
          0.9,
          3 + Math.random() * 3,        // stretch 3-6x — long streaks
        );
      }
    };
    if (w === 0) waveSpawn();
    else core.pendingBlasts.push({ timestamp: Date.now() + 8, callback: waveSpawn });
  }

  // === Phase 3: Sparks — wider cone, gravity, arcing, fiery ===
  // Sakurai: offset spawns + "smaller is more" (tiny fast particles read as dense)
  for (let i = 0; i < M.SPARK_COUNT; i++) {
    const spread = (Math.random() - 0.5) * 2 * M.SPARK_SPREAD;
    const sparkAngle = angle + spread;
    const speed = 6 + Math.random() * 12;
    const r = Math.random();
    const sparkColor = r < 0.35 ? color       // player identity
      : r < 0.55 ? warm(color, 0.5)            // warm
        : r < 0.75 ? lighten(warm(color, 0.4), 0.3)  // bright warm
          : r < 0.9 ? lighten(color, 0.5)       // hot bright
            : darken(warm(color, 0.5), 0.3);       // deep warm ember
    // Offset 3-8px along spark direction
    const off = 3 + Math.random() * 5;
    core.spawnParticleAtAngle(
      x + Math.cos(sparkAngle) * off,
      y + Math.sin(sparkAngle) * off,
      sparkAngle, speed,
      sparkColor,
      0.2 + Math.random() * 0.2,    // 200-400ms — linger a bit
      0.25 + Math.random() * 0.4,   // small-medium
      0.25 + Math.random() * 0.15,  // gravity pulls down
      0.95,
      true, true,
      0.85,
    );
  }

  // === Sakurai: "Smaller is more" — micro-particles for perceived density ===
  // Tiny fast particles that read as "lots of debris" for near-zero budget
  const microCount = 8;
  for (let i = 0; i < microCount; i++) {
    const microSpread = (Math.random() - 0.5) * 2 * M.SPARK_SPREAD;
    const microAngle = angle + microSpread;
    const microSpeed = 4 + Math.random() * 10;
    const microColor = Math.random() < 0.5 ? color : lighten(warm(color, 0.4), 0.3);
    const off = 2 + Math.random() * 6;
    core.spawnParticleAtAngle(
      x + Math.cos(microAngle) * off,
      y + Math.sin(microAngle) * off,
      microAngle, microSpeed,
      microColor,
      0.08 + Math.random() * 0.1,   // very brief — 80-180ms
      0.08 + Math.random() * 0.12,  // tiny scale
      0.1 + Math.random() * 0.1,    // light gravity
      0.92,
      true, true,                    // ADD, hard — crisp tiny dots
      0.7,
    );
  }

  // === Sakurai: multi-element billboard — one sprite reads as many ===
  // 2 cluster-texture particles in the cone for cheap perceived density
  for (let i = 0; i < 2; i++) {
    const clusterAngle = angle + (Math.random() - 0.5) * M.CONE_SPREAD;
    const clusterSpeed = 6 + Math.random() * 8;
    const off = 5 + Math.random() * 5;
    const p = core.getInactive();
    if (!p) continue;

    configParticle(p, core, {
      x: x + Math.cos(clusterAngle) * off,
      y: y + Math.sin(clusterAngle) * off,
      vx: Math.cos(clusterAngle) * clusterSpeed,
      vy: Math.sin(clusterAngle) * clusterSpeed,
      color: Math.random() < 0.5 ? color : lighten(warm(color, 0.4), 0.3),
      life: 0.12 + Math.random() * 0.1,
      scale: 0.5 + Math.random() * 0.4,
      alpha: 0.8,
      gravity: 0.05,
      friction: 0.94,
      hard: false,
    });
    p.sprite.texture = core.clusterTexture;  // Sakurai billboard trick
  }

  // === Phase 4: Smoke wisps — delayed baton pass ===
  // Sakurai: "Like a baton pass, increasing the dominance of the smoke"
  // Offset backward from turret so smoke doesn't cover the flash
  core.pendingBlasts.push({
    timestamp: Date.now() + M.SMOKE_DELAY,
    callback: () => {
      const backAngle = angle + Math.PI; // opposite fire direction
      for (let i = 0; i < M.SMOKE_COUNT; i++) {
        const smokeSpread = (Math.random() - 0.5) * Math.PI * 0.5;
        const smokeAngle = backAngle + smokeSpread;
        const r = Math.random();
        const smokeColor = r < 0.3 ? 0x332211
          : r < 0.5 ? 0x443322
            : r < 0.7 ? 0x333344
              : 0x222233;
        // Offset 3-8px backward so smoke originates behind the flash
        const off = 3 + Math.random() * 5;
        core.spawnParticleAtAngle(
          x + Math.cos(smokeAngle) * off,
          y + Math.sin(smokeAngle) * off,
          smokeAngle,
          0.8 + Math.random() * 2.5,  // drift backward
          smokeColor,
          0.3 + Math.random() * 0.3,  // 300-600ms — visible smoke trail
          1.5 + Math.random() * 1.5,  // large smoke puffs
          -0.015,                      // float up
          0.982,
          false, false,                // NORMAL, soft — dark smoke
          0.5,
        );
      }
    },
  });

  // === Shockwave rings at muzzle — layered ===
  core.spawnShockwave(x, y, color, M.SHOCKWAVE_RADIUS, 300, 3, 0.18);
  core.spawnShockwave(x, y, warm(color, 0.3), M.SHOCKWAVE_RADIUS * 0.6, 200, 2, 0.12);

  // === Impact lines — crisp forward streaks (Sakurai contrast: straight vs curves) ===
  const halfCount = Math.floor(M.IMPACT_LINE_COUNT / 2);
  for (let i = 0; i < M.IMPACT_LINE_COUNT; i++) {
    const lineSpread = (i - halfCount) * M.IMPACT_LINE_SPREAD;
    const lineAngle = angle + lineSpread;
    const life = 0.12 + Math.random() * 0.06;
    const dark = Math.random() < 0.25;
    core.impactLines.push({
      x, y, angle: lineAngle,
      innerRadius: 5,
      outerRadius: 28 + Math.random() * 20,
      expandSpeed: 220 + Math.random() * 80,
      color: dark ? darken(color, 0.85) : (Math.random() < 0.4 ? lighten(color, 0.3) : color),
      lineWidth: dark ? 3.5 : 2.5 + Math.random(),
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }
}
